import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import '../templates';
import { TemplateRenderer } from '../templates/TemplateRenderer';
import { registry } from '../templates/registry';
import type { InvitationData } from '../templates/types';
import { ArrowLeft, Sparkles } from 'lucide-react';

export function TemplateDirectPreviewPage() {
  const { templateId = 'video-02-kirtan-divine' } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  // Templates opt in via registry metadata (no hard-coded ids for this path).
  const usesQueryParamPreview = registry.get(templateId)?.metadata.previewFromQueryParams === true;

  const isKirtan = templateId === 'video-02-kirtan-divine';
  const isRoyal = templateId === 'video-01-royal-gold';
  const isRadha = templateId === 'video-03-radha-krishna';
  const isGsapRadha = templateId === 'exp-gsap-radha-krishna';
  const isGsapShiva = templateId === 'exp-gsap-shiva-parvati';
  const isGsap = isGsapRadha || isGsapShiva;

  const mockFutureIsoDate = '2027-11-28T18:00:00.000Z';
  const mockFutureFormattedDate = 'Sunday, 28th November 2027';

  const mockData: InvitationData = {
    templateId,
    occasionType: 'WEDDING',
    eventName: isKirtan || isRadha || isGsap ? 'Raghav & Riya' : isRoyal ? 'Aarav & Ananya' : 'Shubh Vivah',
    eventDate: mockFutureIsoDate,
    eventVenue: isRadha || isGsap ? 'Vrindavan Garden Banquets, Pune' : 'Hallmark Banquets, Anand Vihar, Delhi - 110092',
    hostName: 'Mrs. Mansi Chopra & Mr. Lalit Chopra',
    mediaUrls: isGsapRadha
      ? [
          '/assets/templates/video-03-radha-krishna/royal-couple.webp',
          '/assets/templates/video-03-radha-krishna/blue-couple.webp',
          '/assets/templates/video-03-radha-krishna/green-couple.webp',
          '/assets/templates/video-03-radha-krishna/yellow-couple.webp',
          '/assets/templates/video-03-radha-krishna/haldi-procession.webp',
          '/assets/templates/video-03-radha-krishna/radha-krishna-garden.webp',
        ]
      : isGsapShiva
      ? [
          '/assets/templates/exp-shiva-parvati/shiva_parvati_hero_bg.jpg',
          '/assets/templates/exp-shiva-parvati/shiva_blessing_bg.jpg',
          '/assets/templates/exp-shiva-parvati/kailash_mandap_bg.jpg',
        ]
      : undefined,
    customData: {
      wedding: {
        groom: {
          fullName: isKirtan || isRadha || isGsap ? 'Raghav Chopra' : 'Aarav Sharma',
          nickname: isKirtan || isRadha || isGsap ? 'Raghav' : 'Aarav',
          photoUrl: isGsapRadha
            ? '/assets/templates/video-03-radha-krishna/royal-couple.webp'
            : isGsapShiva
            ? '/assets/templates/exp-shiva-parvati/shiva_blessing_bg.jpg'
            : undefined,
        },
        bride: {
          fullName: isKirtan || isRadha || isGsap ? 'Riya Kapoor' : 'Ananya Mehta',
          nickname: isKirtan || isRadha || isGsap ? 'Riya' : 'Ananya',
          photoUrl: isGsapRadha
            ? '/assets/templates/video-03-radha-krishna/blue-couple.webp'
            : isGsapShiva
            ? '/assets/templates/exp-shiva-parvati/kailash_mandap_bg.jpg'
            : undefined,
        },
      },
      ...(isRadha || isGsap
        ? {
            venueAddress: 'Vrindavan Garden Banquets, Pune',
          }
        : {}),
      experience: {
        version: '1.0',
        template: 'royal',
        plan: 'basic',
        rituals: [],
        meta: {
          groomName: isKirtan || isRadha || isGsap ? 'Raghav' : 'Aarav',
          brideName: isKirtan || isRadha || isGsap ? 'Riya' : 'Ananya',
          weddingDate: mockFutureFormattedDate,
          eventTime: '06:00 PM onwards',
          hostName: 'Chopra and Kapoor Families',
          tagline: isRadha || isGsap ? 'Two hearts meet under the grace of Radha Krishna.' : undefined,
          referenceImages: {},
        },
      } as any,
    },
  };

  // Query-param templates (e.g. Marathi Akshata 3D): honest placeholders only (the
  // template shows "Bride Name" / "Groom Name" / "Sample date" when a field is empty).
  // Fields can be tried from the URL: ?bride=…&groom=…&date=2027-02-14&time=…&venue=…&rsvp=https://…
  const previewData: InvitationData = usesQueryParamPreview
    ? {
        templateId,
        occasionType: 'WEDDING',
        eventName: searchParams.get('title') ?? 'Wedding Ceremony',
        eventDate: searchParams.get('date') ?? mockFutureIsoDate,
        eventTime: searchParams.get('time') ?? undefined,
        eventVenue: searchParams.get('venue') ?? '',
        hostName: '',
        rsvpLink: searchParams.get('rsvp') ?? undefined,
        customData: {
          wedding: {
            bride: { fullName: searchParams.get('bride') ?? '' },
            groom: { fullName: searchParams.get('groom') ?? '' },
          },
        },
      }
    : mockData;

  const templateMeta = registry.get(templateId)?.metadata;
  const templateDisplayName =
    templateMeta?.name ??
    templateId
      .replace(/^(video|exp)-/, '')
      .replace(/^\d+-/, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="relative min-h-screen bg-black text-white">
      {/* Floating Navigation Banner: bottom bar on mobile to avoid overlapping template header/Ganesh nav, top left on desktop */}
      <div className="fixed bottom-4 left-4 right-4 sm:bottom-auto sm:top-3 sm:left-3 sm:right-auto z-50 flex items-center justify-between sm:justify-start gap-2 pointer-events-none">
        <Link
          to="/catalog"
          className="pointer-events-auto flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-black/85 border border-amber-500/50 text-amber-300 text-xs font-semibold backdrop-blur-md shadow-xl hover:bg-amber-500/20 transition-all"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to designs
        </Link>
        <div className="pointer-events-auto flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-black/85 sm:bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-semibold backdrop-blur-md shadow-xl">
          <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Previewing {templateDisplayName}
        </div>
      </div>

      <TemplateRenderer templateId={templateId} data={previewData} />
    </div>
  );
}
